import type { ComponentType, ReactElement, ReactNode } from 'react'
import type { LucideProps } from 'lucide-react'
import { Eye, Layers, Minus, Plus, Trash2 } from 'lucide-react'

type Icon = ComponentType<LucideProps>

interface MenuItemProps {
  onClick: () => void
  disabled?: boolean
  className?: string
  title?: string
  children: ReactNode
}

function MenuItem({ onClick, disabled, className, title, children }: MenuItemProps): ReactElement {
  const baseClass = 'w-full flex items-center gap-2 px-3 py-2 text-sm'
  const enabled = !disabled
  const stateClass = enabled
    ? (className ?? 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700')
    : 'text-slate-400 dark:text-slate-500 cursor-not-allowed'
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClass} ${stateClass}`}
      title={title}
    >
      {children}
    </button>
  )
}

interface ToggleItemProps {
  isInSet: boolean
  addLabel: string
  removeLabel: string
  onClick: () => void
}

export function ToggleItem({
  isInSet,
  addLabel,
  removeLabel,
  onClick,
}: ToggleItemProps): ReactElement {
  return (
    <MenuItem onClick={onClick}>
      {isInSet ? (
        <>
          <Minus className="h-4 w-4 text-slate-400" />
          <span>{removeLabel}</span>
        </>
      ) : (
        <>
          <Plus className="h-4 w-4 text-slate-400" />
          <span>{addLabel}</span>
        </>
      )}
    </MenuItem>
  )
}

interface AddSelectedItemProps {
  hasSelection: boolean
  selectionCount: number
  label: string
  enabledTitle: string
  disabledTitle: string
  onClick: () => void
}

export function AddSelectedItem(props: AddSelectedItemProps): ReactElement {
  const { hasSelection, selectionCount, label, enabledTitle, disabledTitle, onClick } = props
  return (
    <MenuItem
      onClick={onClick}
      disabled={!hasSelection}
      title={hasSelection ? enabledTitle : disabledTitle}
    >
      <Layers className="h-4 w-4" />
      <span>{label}</span>
      {hasSelection && (
        <span className="ml-auto text-xs text-fuchsia-600 dark:text-fuchsia-400">
          ({selectionCount})
        </span>
      )}
    </MenuItem>
  )
}

interface ViewSetItemProps {
  label: string
  count: number
  onClick: () => void
}

export function ViewSetItem({ label, count, onClick }: ViewSetItemProps): ReactElement {
  return (
    <MenuItem onClick={onClick}>
      <Eye className="h-4 w-4 text-slate-400" />
      <span>{label}</span>
      {count > 0 && <span className="ml-auto text-xs text-slate-400">({count})</span>}
    </MenuItem>
  )
}

interface ClearSetItemProps {
  label: string
  count: number
  onClick: () => void
}

export function ClearSetItem({ label, count, onClick }: ClearSetItemProps): ReactElement {
  const enabledClass = 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50'
  return (
    <MenuItem onClick={onClick} disabled={count === 0} className={enabledClass}>
      <Trash2 className="h-4 w-4" />
      <span>{label}</span>
    </MenuItem>
  )
}

export function MenuDivider(): ReactElement {
  return <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
}

interface MenuFooterProps {
  icon: Icon
  message: string
}

export function MenuFooter({ icon: IconComponent, message }: MenuFooterProps): ReactElement {
  return (
    <div className="mt-1 border-t border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/50">
      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <IconComponent className="h-3 w-3" />
        <span>{message}</span>
      </div>
    </div>
  )
}
